import type { FocusEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  ConfirmationModal,
  FormField,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  RichDescription,
  Text
} from '@percent/lemonade'
import {
  canLogGivingAction,
  isGivingActionCompleted,
  OrganisationChip
} from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCard'
import { LoggedDaysCalendar } from '@percent/workplace-giving/common/components/LoggedDaysCalendar/LoggedDaysCalendar'
import classes from './GivingActionCompletionModal.module.scss'
import type { GivingActionCompletionModalProps } from './GivingActionCompletionModal.types'

export function GivingActionCompletionModal({
  open,
  row,
  selectedDate,
  onSelectedDateChange,
  onClose,
  onConfirm,
  loading = false,
  loggedDates,
  minDate,
  maxDate,
  DatePickerComponent = LoggedDaysCalendar
}: GivingActionCompletionModalProps) {
  const { t } = useTranslation()

  // The same modal doubles as the detail view for every action: one-off actions log the current
  // period so they get no date picker, and an action with nothing left to log is read-only.
  const isRecurring = row?.frequency === 'daily'
  const isCompleted = !!row && isGivingActionCompleted(row)
  const isViewOnly = !!row && !canLogGivingAction(row)
  const confirmLabel = isRecurring
    ? t('workplace_giving.givingActions.completionModal.confirm')
    : t('workplace_giving.givingActions.markAsDone')

  const summary = row ? (
    <div className={classes.Action}>
      <Text size="small" className={classes.ActionName}>
        {row.name}
      </Text>
      {row.organisation ? (
        <div className={classes.ActionOrganisation} data-testid="giving-action-completion-organisation">
          <OrganisationChip name={row.organisation.name} logo={row.organisation.logoUrl} />
        </div>
      ) : null}
      <div className={classes.ActionDescription}>
        <RichDescription content={row.description} testId="giving-action-completion-description" />
      </div>
    </div>
  ) : null

  if (isViewOnly) {
    return (
      <Modal borderRadiiSize="xlarge" open={open} onClose={onClose}>
        <ModalHeader>
          <ModalTitle>
            {isCompleted
              ? t('workplace_giving.givingActions.completed')
              : t('workplace_giving.givingActions.completionModal.unavailableTitle')}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className={classes.Content} data-testid="giving-action-completion-view-only">
            {summary}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" shape="oval" onClick={onClose} data-testid="giving-action-completion-close">
            {t('workplace_giving.common.close')}
          </Button>
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Modal borderRadiiSize="xlarge" open={open} onClose={onClose}>
      <ConfirmationModal
        buttonShape="oval"
        title={isRecurring ? t('workplace_giving.givingActions.completionModal.title') : confirmLabel}
        primaryButtonText={confirmLabel}
        secondaryButtonText={t('workplace_giving.common.cancel')}
        handleClose={onClose}
        handleSubmit={() => {
          if (row) onConfirm(row, selectedDate)
        }}
        loading={loading}
        disabled={!row}
        primaryBtnTestId="giving-action-completion-confirm"
        secondaryBtnTestId="giving-action-completion-cancel"
      >
        <div className={classes.Content}>
          {summary}
          {isRecurring ? (
            <>
              <Text size="small">{t('workplace_giving.givingActions.completionModal.description')}</Text>
              <FormField label={t('workplace_giving.givingActions.completionModal.dateLabel')} necessity="required">
                <DatePickerComponent
                  fieldName="givingActionCompletionDate"
                  data-testid="giving-action-completion-date-picker"
                  placeholder={t('workplace_giving.givingActions.completionModal.datePlaceholder')}
                  value={selectedDate}
                  loggedDates={loggedDates}
                  minDate={minDate}
                  maxDate={maxDate}
                  handleChange={date => {
                    if (date instanceof Date) onSelectedDateChange(date)
                  }}
                  handleBlur={(_event: FocusEvent<HTMLButtonElement>) => undefined}
                  error={false}
                  withinPopup
                />
              </FormField>
            </>
          ) : null}
        </div>
      </ConfirmationModal>
    </Modal>
  )
}
